import React, { useState, useEffect } from "react";
import { ShieldCheck, BookOpenCheck, Info } from "lucide-react";
import { getCompetencyMetrics, CompetencyMetricsData } from "../../api/analyticsApi";

interface CompetencyMetricsPanelProps {
  organisationId: string;
}

function getCompetencyColor(pct: number): { bg: string; text: string; border: string } {
  if (pct >= 60) return { bg: "bg-green-50", text: "text-green-700", border: "border-green-200" };
  if (pct >= 30) return { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" };
  return { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" };
}

function getCoverageColor(pct: number): { bg: string; text: string; border: string } {
  if (pct >= 70) return { bg: "bg-green-50", text: "text-green-700", border: "border-green-200" };
  if (pct >= 40) return { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" };
  return { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" };
}

const CompetencyMetricsPanel: React.FC<CompetencyMetricsPanelProps> = ({
  organisationId,
}) => {
  const [metrics, setMetrics] = useState<CompetencyMetricsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCompetencyMetrics(organisationId)
      .then((data) => {
        if (!cancelled) setMetrics(data);
      })
      .catch(() => {
        // Silently fail — panel just won't render
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [organisationId]);

  if (loading || !metrics) return null;

  // Two conditions where the % metric is undefined:
  //   coreCoursesCount === 0   → org has no is_core master courses assigned
  //   denominator === 0        → core courses assigned, but to a group
  //                              tag no active staff has (Codex R2 edge)
  // Both render the neutral state — same look, distinct copy so admins
  // know whether to assign a course or assign staff to the group.
  const noCoreScope = metrics.coreAICompetency.coreCoursesCount === 0;
  const noStaffInScope =
    !noCoreScope && metrics.coreAICompetency.denominator === 0;
  const noScope = noCoreScope || noStaffInScope;
  const noScopeCopy = noCoreScope
    ? "No core courses assigned"
    : "No staff in scope";
  const competencyColors = noScope
    ? { bg: "bg-slate-50", text: "text-slate-600", border: "border-slate-200" }
    : getCompetencyColor(metrics.coreAICompetency.pct);
  const coverageColors = getCoverageColor(metrics.aiTopicCoverage.pct);

  return (
    <div className="mb-4 flex items-center gap-4 flex-wrap">
      {/* Core AI Competency */}
      <div
        className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${competencyColors.bg} ${competencyColors.border}`}
      >
        <ShieldCheck size={16} className={competencyColors.text} />
        <div className="flex items-center gap-1">
          <span className={`text-sm font-semibold ${competencyColors.text}`}>
            Core AI Competency:
          </span>
          <span
            tabIndex={0}
            title="Percentage of active staff holding certificates for every core course assigned to your school."
            aria-label="Core AI Competency definition: percentage of active staff holding certificates for every core course assigned to your school."
            className="inline-flex text-slate-500"
          >
            <Info size={14} aria-hidden="true" />
          </span>
          {noScope ? (
            <span className={`text-sm font-medium ${competencyColors.text}`}>
              {noScopeCopy}
            </span>
          ) : (
            <>
              <span className={`text-sm font-bold ${competencyColors.text}`}>
                {metrics.coreAICompetency.pct}%
              </span>
              {metrics.coreAICompetency.pct >= 60 && (
                <span className="ml-1 text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full font-medium">
                  Milestone
                </span>
              )}
            </>
          )}
        </div>
        {!noScope && (
          <span className="text-xs text-gray-500 ml-1">
            {metrics.coreAICompetency.numerator} of{" "}
            {metrics.coreAICompetency.denominator} staff
          </span>
        )}
      </div>

      {/* AI Topic Coverage */}
      <div
        className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${coverageColors.bg} ${coverageColors.border}`}
      >
        <BookOpenCheck size={16} className={coverageColors.text} />
        <div className="flex items-center gap-1">
          <span className={`text-sm font-semibold ${coverageColors.text}`}>
            AI Topic Coverage:
          </span>
          <span
            tabIndex={0}
            title="Percentage of your staff's unit completions that are on the current course content."
            aria-label="AI Topic Coverage definition: percentage of your staff's unit completions that are on the current course content."
            className="inline-flex text-slate-500"
          >
            <Info size={14} aria-hidden="true" />
          </span>
          <span className={`text-sm font-bold ${coverageColors.text}`}>
            {metrics.aiTopicCoverage.pct}%
          </span>
        </div>
        <span className="text-xs text-gray-500 ml-1">
          Coverage across all assigned courses
        </span>
      </div>
    </div>
  );
};

export default CompetencyMetricsPanel;
